import { create } from 'zustand';
import {
  createPlan as apiCreatePlan,
  getPlanDetail as apiGetPlanDetail,
  listPlansForPatient as apiListPlansForPatient,
  applyDiscount as apiApplyDiscount,
  createMultiItemPlan as apiCreateMultiItemPlan,
  cancelPlan as apiCancelPlan,
  listActivePlansForPatient as apiListActivePlansForPatient,
  listRemainingPlanItems as apiListRemainingPlanItems,
  startVisitSittingForItem as apiStartVisitSittingForItem,
  attachPlanItemsToVisit as apiAttachPlanItemsToVisit,
  scheduleAppointmentForItem as apiScheduleAppointmentForItem,
  fetchPlanDetail as apiFetchPlanDetailRich,
  listPatientPlans as apiListPatientPlans,
  collectUpfrontPayment as apiCollectUpfrontPayment,
  startAndCollect as apiStartAndCollect,
  startPrepaid as apiStartPrepaid,
  editStep as apiEditStep,
  cancelStep as apiCancelStep,
  deletePlan as apiDeletePlan,
} from '../services/api/patientPlanApi';
import { realtimeMiddleware } from './realtimeMiddleware';
import { REALTIME_EVENTS } from '../services/realtime/RealtimeSyncService';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * Treatment Plan Store — plan-level state.
 *
 * Keeps:
 *   - `activePlan`: the plan currently open in the UI, with its items +
 *     sittings + payments.
 *   - `plansByPatient`: cache keyed by patient_id → PlanResponse[]. Used
 *     by the Procedure Queue + patient-arrival banner (Phase 1 projection,
 *     status-filtered server-side).
 *   - `activePlansByPatient` (Phase 2): keyed by patient_id → full
 *     PlanDetailResponse[], only non-terminal plans. Registration screen
 *     reads this to render ActivePlanSuggestionCard.
 *   - `remainingItemsByPlan` (Phase 2): keyed by plan_id → open PlanItems.
 *
 * Realtime:
 *   - PLAN_CREATED / PLAN_STATUS_CHANGED → patch active plan + invalidate
 *     the relevant patient cache entry.
 *   - PLAN_ITEM_CONSUMED → drop the consumed item from remainingItemsByPlan
 *     + decrement its count on the active-plans projection.
 *   - PLAN_COMPLETED → drop plan from activePlansByPatient.
 *   - PLAN_CANCELLED → drop plan from activePlansByPatient + mark status.
 * ═══════════════════════════════════════════════════════════════════════
 */

const planEventHandlers = {
  [REALTIME_EVENTS.PLAN_CREATED]: (state, event) => {
    const payload = event.payload || {};
    const patientId = payload.patient_id;
    if (!patientId) return {};
    // Invalidate caches so the next list calls refetch.
    const cache = { ...state.plansByPatient };
    delete cache[patientId];
    const active = { ...state.activePlansByPatient };
    delete active[patientId];
    return { plansByPatient: cache, activePlansByPatient: active };
  },

  [REALTIME_EVENTS.PLAN_STATUS_CHANGED]: (state, event) => {
    const payload = event.payload || {};
    const { plan_id: planId, status } = payload;
    if (!planId || !status) return {};

    const patches = {};
    if (state.activePlan?.plan?.plan_id === planId) {
      patches.activePlan = {
        ...state.activePlan,
        plan: { ...state.activePlan.plan, status },
      };
    }
    const updatedCache = { ...state.plansByPatient };
    for (const [pid, plans] of Object.entries(updatedCache)) {
      if (plans.some((p) => p.plan_id === planId)) {
        updatedCache[pid] = plans.map((p) =>
          p.plan_id === planId ? { ...p, status } : p,
        );
      }
    }
    if (Object.keys(updatedCache).length) patches.plansByPatient = updatedCache;
    return patches;
  },

  [REALTIME_EVENTS.PLAN_ITEM_CONSUMED]: (state, event) => {
    const payload = event.payload || {};
    const { plan_id: planId, item_id: itemId, remaining_open_items } = payload;
    if (!planId || !itemId) return {};

    const patches = {};

    // Drop the consumed item from the remainingItemsByPlan projection.
    if (state.remainingItemsByPlan?.[planId]) {
      const updated = state.remainingItemsByPlan[planId].filter(
        (it) => it.item_id !== itemId,
      );
      patches.remainingItemsByPlan = {
        ...state.remainingItemsByPlan,
        [planId]: updated,
      };
    }

    // Update the active plans projection's open_item_count denorm.
    if (state.activePlansByPatient && typeof remaining_open_items === 'number') {
      const updated = { ...state.activePlansByPatient };
      for (const [pid, plans] of Object.entries(updated)) {
        const next = plans.map((pd) => {
          if (pd.plan?.plan_id !== planId) return pd;
          return {
            ...pd,
            plan: { ...pd.plan, open_item_count: remaining_open_items },
          };
        });
        updated[pid] = next;
      }
      patches.activePlansByPatient = updated;
    }

    return patches;
  },

  [REALTIME_EVENTS.PLAN_COMPLETED]: (state, event) => {
    const payload = event.payload || {};
    const { plan_id: planId, patient_id: patientId } = payload;
    if (!planId) return {};
    const patches = {};
    if (patientId && state.activePlansByPatient?.[patientId]) {
      const filtered = state.activePlansByPatient[patientId].filter(
        (pd) => pd.plan?.plan_id !== planId,
      );
      patches.activePlansByPatient = {
        ...state.activePlansByPatient,
        [patientId]: filtered,
      };
    }
    // Patch activePlan if it's the one that just completed.
    if (state.activePlan?.plan?.plan_id === planId) {
      patches.activePlan = {
        ...state.activePlan,
        plan: { ...state.activePlan.plan, status: 'completed' },
      };
    }
    return patches;
  },

  [REALTIME_EVENTS.PLAN_CANCELLED]: (state, event) => {
    const payload = event.payload || {};
    const { plan_id: planId, patient_id: patientId } = payload;
    if (!planId) return {};
    const patches = {};
    if (patientId && state.activePlansByPatient?.[patientId]) {
      const filtered = state.activePlansByPatient[patientId].filter(
        (pd) => pd.plan?.plan_id !== planId,
      );
      patches.activePlansByPatient = {
        ...state.activePlansByPatient,
        [patientId]: filtered,
      };
    }
    if (state.activePlan?.plan?.plan_id === planId) {
      patches.activePlan = {
        ...state.activePlan,
        plan: {
          ...state.activePlan.plan,
          status: 'cancelled',
          cancellation_reason: payload.cancellation_reason,
          cancelled_by_member_id: payload.cancelled_by_member_id,
        },
      };
    }
    return patches;
  },
};

const usePatientPlanStore = create(
  realtimeMiddleware(
    (set, get) => ({
      // ── State ────────────────────────────────────────────────────────
      activePlan: null,
      plansByPatient: {},
      // Phase 2
      activePlansByPatient: {},
      remainingItemsByPlan: {},
      // Phase 2.8 — PatientPlanPage rich detail + clinic-wide index
      planDetail: null,        // { plan_id, payment_mode, ..., steps: [...] }
      planIndexRows: [],
      isLoading: false,
      isLoadingDetail: false,
      isLoadingIndex: false,
      isCreating: false,
      isCancelling: false,
      error: null,

      // ── Create (Phase 1, single-visit) ───────────────────────────────
      createPlan: async (body) => {
        set({ isCreating: true, error: null });
        const result = await apiCreatePlan(body);
        if (result.success) {
          set({ activePlan: result.data, isCreating: false });
          const patientId = body.patient_id;
          if (patientId) {
            const cache = { ...get().plansByPatient };
            delete cache[patientId];
            set({ plansByPatient: cache });
          }
        } else {
          set({ error: result.error, isCreating: false });
        }
        return result;
      },

      // ── Create multi-item plan (Phase 2) ─────────────────────────────
      createMultiItemPlan: async (body) => {
        set({ isCreating: true, error: null });
        const result = await apiCreateMultiItemPlan(body);
        if (result.success) {
          set({ activePlan: result.data, isCreating: false });
          const patientId = body.patient_id;
          if (patientId) {
            const plansCache = { ...get().plansByPatient };
            delete plansCache[patientId];
            const activeCache = { ...get().activePlansByPatient };
            delete activeCache[patientId];
            set({
              plansByPatient: plansCache,
              activePlansByPatient: activeCache,
            });
          }
        } else {
          set({ error: result.error, isCreating: false });
        }
        return result;
      },

      // ── Cancel plan (Phase 2) ────────────────────────────────────────
      cancelPlan: async (planId, { reason } = {}) => {
        set({ isCancelling: true, error: null });
        const result = await apiCancelPlan(planId, { reason });
        if (result.success) {
          // Optimistically patch activePlan + all known projections. The
          // PLAN_CANCELLED WS event will overwrite with the server's truth.
          const current = get().activePlan;
          if (current?.plan?.plan_id === planId) {
            set({
              activePlan: {
                ...current,
                plan: { ...current.plan, ...result.data },
              },
            });
          }
          // Drop from active-plans projections.
          const updated = { ...get().activePlansByPatient };
          for (const [pid, plans] of Object.entries(updated)) {
            updated[pid] = plans.filter((pd) => pd.plan?.plan_id !== planId);
          }
          set({ activePlansByPatient: updated, isCancelling: false });
        } else {
          set({ error: result.error, isCancelling: false });
        }
        return result;
      },

      // ── Load plan detail ─────────────────────────────────────────────
      fetchPlanDetail: async (planId) => {
        set({ isLoading: true, error: null });
        const result = await apiGetPlanDetail(planId);
        if (result.success) {
          set({ activePlan: result.data, isLoading: false });
        } else {
          set({ error: result.error, isLoading: false });
        }
        return result;
      },

      clearActivePlan: () => set({ activePlan: null }),

      // ── Phase 1 patient list (status-filtered, plan rows only) ───────
      fetchPlansForPatient: async (patientId, { status, force = false } = {}) => {
        if (!force) {
          const cached = get().plansByPatient[patientId];
          if (cached) return { success: true, data: { plans: cached, count: cached.length } };
        }
        set({ isLoading: true, error: null });
        const result = await apiListPlansForPatient(patientId, { status });
        if (result.success) {
          const plans = result.data?.plans || [];
          set((s) => ({
            plansByPatient: { ...s.plansByPatient, [patientId]: plans },
            isLoading: false,
          }));
        } else {
          set({ error: result.error, isLoading: false });
        }
        return result;
      },

      // ── Phase 2 — non-terminal plans with full details (registration) ─
      fetchActivePlansForPatient: async (patientId, { force = false } = {}) => {
        if (!force) {
          const cached = get().activePlansByPatient[patientId];
          if (cached) return { success: true, data: { plans: cached, count: cached.length, patient_id: patientId } };
        }
        set({ isLoading: true, error: null });
        const result = await apiListActivePlansForPatient(patientId);
        if (result.success) {
          const plans = result.data?.plans || [];
          set((s) => ({
            activePlansByPatient: { ...s.activePlansByPatient, [patientId]: plans },
            isLoading: false,
          }));
        } else {
          set({ error: result.error, isLoading: false });
        }
        return result;
      },

      // ── Phase 2 — open items of a plan (suggestion source) ───────────
      fetchRemainingItems: async (planId, { force = false } = {}) => {
        if (!force) {
          const cached = get().remainingItemsByPlan[planId];
          if (cached) return { success: true, data: { items: cached, count: cached.length, plan_id: planId } };
        }
        const result = await apiListRemainingPlanItems(planId);
        if (result.success) {
          const items = result.data?.items || [];
          set((s) => ({
            remainingItemsByPlan: { ...s.remainingItemsByPlan, [planId]: items },
          }));
        }
        return result;
      },

      // ── Phase 2 — start a sitting for a plan item bound to a visit ───
      startVisitSittingForItem: async (planId, itemId, opts) => {
        const result = await apiStartVisitSittingForItem(planId, itemId, opts);
        if (result.success) {
          // The item is now linked, so remove from remainingItemsByPlan.
          const existing = get().remainingItemsByPlan[planId];
          if (existing) {
            set((s) => ({
              remainingItemsByPlan: {
                ...s.remainingItemsByPlan,
                [planId]: existing.filter((it) => it.item_id !== itemId),
              },
            }));
          }
        }
        return result;
      },

      // ── Phase 2 — attach multiple plan items to a visit at registration ─
      attachPlanItemsToVisit: async (visitId, payload) => {
        return apiAttachPlanItemsToVisit(visitId, payload);
      },

      // ── Phase 2.5 — book a calendar slot for an already-scheduled item ─
      scheduleAppointmentForItem: async (planId, itemId, payload) => {
        return apiScheduleAppointmentForItem(planId, itemId, payload);
      },

      // ── Apply discount on an existing item ───────────────────────────
      applyDiscount: async (planId, itemId, { discount_amount, discount_reason }) => {
        const result = await apiApplyDiscount(planId, itemId, {
          discount_amount,
          discount_reason,
        });
        if (result.success && get().activePlan?.plan?.plan_id === planId) {
          const current = get().activePlan;
          const patchedItems = (current.items || []).map((it) =>
            it.item_id === itemId ? { ...it, ...result.data } : it,
          );
          set({ activePlan: { ...current, items: patchedItems } });
        }
        return result;
      },

      // ── Phase 2.8 — PatientPlanPage rich detail ──────────────────────
      fetchPlanRichDetail: async (planId) => {
        set({ isLoadingDetail: true, error: null });
        const result = await apiFetchPlanDetailRich(planId);
        if (result.success) {
          set({ planDetail: result.data, isLoadingDetail: false });
        } else {
          set({ error: result.error, isLoadingDetail: false });
        }
        return result;
      },

      clearPlanDetail: () => set({ planDetail: null }),

      // ── Phase 2.8 — clinic-wide index ────────────────────────────────
      fetchPlanIndex: async (filters = {}) => {
        set({ isLoadingIndex: true, error: null });
        const result = await apiListPatientPlans(filters);
        if (result.success) {
          set({
            planIndexRows: result.data?.plans || [],
            isLoadingIndex: false,
          });
        } else {
          set({ error: result.error, isLoadingIndex: false });
        }
        return result;
      },

      // ── Phase 2.8 — plan-level + per-step actions ────────────────────
      // Phase 2.9 — actions no longer auto-refetch the full plan. Pre-fix,
      // every action awaited fetchPlanRichDetail before returning, which
      // chained N full-plan re-fetches during a bulk Collect & Start
      // (one per step) and made PatientPlanPage feel sluggish. The page
      // calls refresh() once after a successful action set; realtime
      // events still patch in flight.
      collectUpfrontPayment: async (planId, body) =>
        apiCollectUpfrontPayment(planId, body),

      startAndCollect: async (planId, itemId, body) =>
        apiStartAndCollect(planId, itemId, body),

      startPrepaid: async (planId, itemId, body) =>
        apiStartPrepaid(planId, itemId, body),

      editStep: async (planId, itemId, body) =>
        apiEditStep(planId, itemId, body),

      cancelStep: async (planId, itemId, body) =>
        apiCancelStep(planId, itemId, body),

      deletePlan: async (planId, body) => {
        return apiDeletePlan(planId, body);
      },

      clearError: () => set({ error: null }),
    }),
    {
      storeName: 'patientPlan',
      eventHandlers: planEventHandlers,
    },
  ),
);

export default usePatientPlanStore;
